import type { JSX } from 'react';
import type { FieldPathByValue } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import { StringUtils } from 'ts/commons/StringUtils';
import type { FormTextAreaProps } from 'ts/components/Form';
import { FormGroup, FormTextArea } from 'ts/components/Form';

/** Values provided by the PathsFilter component. */
export type PathsFilterValues = {
	/** List of the included paths in the form of Ant patterns. */
	includedPaths: string[];

	/** List of the excluded paths in the form of Ant patterns. */
	excludedPaths: string[];
};

/** Tooltip for the 'Included Paths' label. */
const INCLUDED_PATHS_TOOLTIP =
	'Regex patterns describing the paths for which findings are included. ' +
	'Patterns can be separated by comma or newline. ' +
	'Leave empty to include all paths.';

/** Tooltip for the 'Excluded Paths' label. */
const EXCLUDED_PATHS_TOOLTIP =
	'Regex patterns describing the paths for which findings are excluded. ' +
	'Patterns can be separated by comma or newline.';

/**
 * Filter that allows to enter multiple include and exclude patterns for file paths. The component will only work when
 * wrapped in a FormProvider.
 */
export function PathsFilter(): JSX.Element {
	return (
		<FormGroup grouped as="fieldset">
			<RegexPathFilterField
				data-testid="include-paths-filter"
				label={
					<legend className="!text-[12px] font-bold" title={INCLUDED_PATHS_TOOLTIP}>
						Included Paths
					</legend>
				}
				name="includedPaths"
			/>
			<RegexPathFilterField
				data-testid="exclude-paths-filter"
				label={
					<legend className="!text-[12px] font-bold" title={EXCLUDED_PATHS_TOOLTIP}>
						Excluded Paths
					</legend>
				}
				name="excludedPaths"
			/>
		</FormGroup>
	);
}

type RegexPathFilterFieldProps = FormTextAreaProps & { name: FieldPathByValue<PathsFilterValues, string[]> };

function RegexPathFilterField({ name, ...textAreaProps }: RegexPathFilterFieldProps) {
	const { control } = useFormContext<PathsFilterValues>();
	return (
		<Controller
			name={name}
			control={control}
			render={props => (
				<FormTextArea
					name={name}
					title="A list of Ant patterns separated by commas or newlines."
					rows={2}
					placeholder="a/b/**, c/d/e"
					defaultValue={props.field.value.join(',')}
					onChange={(event, data) => {
						const valueList = (data.value as string)
							.split(/,|\r?\n/)
							.filter(s => !StringUtils.isEmptyOrWhitespace(s))
							.map(s => s.trim());
						props.field.onChange(valueList);
					}}
					{...textAreaProps}
				/>
			)}
		/>
	);
}
